import { eventBus } from '../core/eventBus.js';
import { utils } from '../core/utils.js';

export class ProjectFilter {
  constructor() {
    this.filterButtons = document.querySelectorAll('.project-filters .filter-btn');
    this.publicationButtons = document.querySelectorAll('.publications-filters .filter-btn');
    this.projectCards = document.querySelectorAll('.project-card');
    this.publicationItems = document.querySelectorAll('.publication-item');
    this.projectsContainer = document.querySelector('.projects-grid');
    
    this.activeProjectFilter = 'all';
    this.activePublicationFilter = 'all';
    
    // Pagination settings
    this.currentPage = 1;
    this.projectsPerPage = this.getProjectsPerPage();
    this.filteredProjects = [];
    this.paginationContainer = null;
    
    this.init();
  }
  
  init() {
    if (this.filterButtons.length) {
      this.setupProjectFilters();
      this.createPaginationContainer();
      this.filterProjects('all'); // Initialize with all projects
    }
    
    if (this.publicationButtons.length) {
      this.setupPublicationFilters();
    }
    
    this.setupRelatedProjectLinks();
    this.setupAbstractToggles();
    this.setupResponsivePagination();
  }
  
  getProjectsPerPage() {
    const width = window.innerWidth;
    if (width <= 576) return 1;  // Mobile: 1 per page
    if (width <= 768) return 2;  // Tablet: 2 per page
    if (width <= 992) return 4;  // Small desktop: 4 per page
    return 6; // Desktop: 6 per page
  }
  
  createPaginationContainer() {
    // Create arrows (inside grid for proper centering)
    const prevArrow = document.createElement('button');
    prevArrow.className = 'pagination-arrow pagination-prev';
    prevArrow.setAttribute('aria-label', 'Previous page');
    prevArrow.disabled = true;
    prevArrow.innerHTML = '<i class="fas fa-chevron-left"></i>';

    const nextArrow = document.createElement('button');
    nextArrow.className = 'pagination-arrow pagination-next';
    nextArrow.setAttribute('aria-label', 'Next page');
    nextArrow.innerHTML = '<i class="fas fa-chevron-right"></i>';

    // Insert arrows inside the grid for vertical centering
    this.projectsContainer?.appendChild(prevArrow);
    this.projectsContainer?.appendChild(nextArrow);

    // Create pagination info (after grid)
    const paginationInfoHTML = `
      <div class="projects-pagination">
        <div class="pagination-info">
          <span class="current-page">1</span>
          <span class="separator">/</span>
          <span class="total-pages">1</span>
        </div>
      </div>
    `;
    this.projectsContainer?.insertAdjacentHTML('afterend', paginationInfoHTML);
    this.paginationContainer = document.querySelector('.projects-pagination');

    // Bind pagination events
    this.bindPaginationEvents();
  }
  
  bindPaginationEvents() {
    const prevBtn = this.projectsContainer?.querySelector('.pagination-prev');
    const nextBtn = this.projectsContainer?.querySelector('.pagination-next');
    
    prevBtn?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    nextBtn?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isProjectsInView()) return;
      
      if (e.key === 'ArrowLeft' && !e.target.closest('input, textarea')) {
        this.goToPage(this.currentPage - 1);
      } else if (e.key === 'ArrowRight' && !e.target.closest('input, textarea')) {
        this.goToPage(this.currentPage + 1);
      }
    });
    
    // Swipe gestures for mobile
    this.setupSwipeGestures();
  }
  
  setupSwipeGestures() {
    if (!this.projectsContainer) return;
    
    let touchStartX = 0;
    let touchEndX = 0;
    
    this.projectsContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.projectsContainer.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
    
    const handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next page
          this.goToPage(this.currentPage + 1);
        } else {
          // Swipe right - previous page
          this.goToPage(this.currentPage - 1);
        }
      }
    };
    
    this.handleSwipe = handleSwipe;
  }
  
  setupProjectFilters() {
    this.filterButtons.forEach(button => {
      button.addEventListener('click', () => {
        const filter = button.getAttribute('data-filter');
        this.currentPage = 1; // Reset to first page when filtering
        this.filterProjects(filter);
      });
    });
  }
  
  filterProjects(filter) {
    // Update active button and ARIA state
    this.filterButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-pressed', 'false');
    });
    const activeButton = Array.from(this.filterButtons).find(
      btn => btn.getAttribute('data-filter') === filter
    );
    activeButton?.classList.add('active');
    activeButton?.setAttribute('aria-pressed', 'true');
    
    this.activeProjectFilter = filter;
    
    // Get filtered projects
    this.filteredProjects = Array.from(this.projectCards).filter(card => {
      const categoryAttr = card.getAttribute('data-category');
      if (!categoryAttr) return filter === 'all';
      const categories = categoryAttr.split(' ');
      return filter === 'all' || categories.includes(filter);
    });
    
    // Update pagination
    this.updatePagination();
    
    // Show current page
    this.showCurrentPage();
    
    // Emit event
    eventBus.emit('filter:changed', { type: 'projects', filter, page: this.currentPage });
  }
  
  updatePagination() {
    const totalPages = Math.ceil(this.filteredProjects.length / this.projectsPerPage);
    
    // Update pagination info
    const currentPageSpan = this.paginationContainer?.querySelector('.current-page');
    const totalPagesSpan = this.paginationContainer?.querySelector('.total-pages');
    
    if (currentPageSpan) currentPageSpan.textContent = this.currentPage;
    if (totalPagesSpan) totalPagesSpan.textContent = totalPages;
    
    // Update button states (arrows are now inside the grid)
    const prevBtn = this.projectsContainer?.querySelector('.pagination-prev');
    const nextBtn = this.projectsContainer?.querySelector('.pagination-next');

    if (prevBtn) {
      prevBtn.disabled = this.currentPage === 1;
      prevBtn.style.display = totalPages <= 1 ? 'none' : 'flex';
    }
    if (nextBtn) {
      nextBtn.disabled = this.currentPage >= totalPages;
      nextBtn.style.display = totalPages <= 1 ? 'none' : 'flex';
    }

    // Hide pagination info if only one page
    if (this.paginationContainer) {
      this.paginationContainer.style.display = totalPages <= 1 ? 'none' : 'flex';
    }
  }
  
  showCurrentPage() {
    const startIndex = (this.currentPage - 1) * this.projectsPerPage;
    const endIndex = startIndex + this.projectsPerPage;
    const visibleSet = new Set(this.filteredProjects.slice(startIndex, endIndex));

    // Single pass: hide/show without animation
    this.projectCards.forEach(card => {
      const shouldShow = visibleSet.has(card);
      card.style.display = shouldShow ? 'flex' : 'none';
    });
  }
  
  goToPage(page) {
    const totalPages = Math.ceil(this.filteredProjects.length / this.projectsPerPage);
    
    if (page < 1 || page > totalPages) return;
    
    this.currentPage = page;
    this.updatePagination();
    this.showCurrentPage();
    
    // Emit event
    eventBus.emit('pagination:changed', { page: this.currentPage, total: totalPages });
  }
  
  setupResponsivePagination() {
    const handleResize = utils.debounce(() => {
      const newProjectsPerPage = this.getProjectsPerPage();
      
      if (newProjectsPerPage !== this.projectsPerPage) {
        this.projectsPerPage = newProjectsPerPage;
        this.currentPage = 1; // Reset to first page
        this.updatePagination();
        this.showCurrentPage();
      }
    }, 250);
    
    window.addEventListener('resize', handleResize);
  }
  
  isProjectsInView() {
    const projectsSection = document.querySelector('#projects');
    if (!projectsSection) return false;
    
    const rect = projectsSection.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }
  
  setupPublicationFilters() {
    this.publicationButtons.forEach(button => {
      button.addEventListener('click', () => {
        const filter = button.getAttribute('data-filter');
        this.filterPublications(filter);
      });
    });
  }
  
  filterPublications(filter) {
    // Update active button
    this.publicationButtons.forEach(btn => btn.classList.remove('active'));
    const activeButton = Array.from(this.publicationButtons).find(
      btn => btn.getAttribute('data-filter') === filter
    );
    activeButton?.classList.add('active');
    
    this.activePublicationFilter = filter;
    
    // Filter items with animation
    this.publicationItems.forEach(item => {
      const category = item.getAttribute('data-category');
      const shouldShow = filter === 'all' || category === filter;
      
      if (shouldShow) {
        this.showItem(item);
      } else {
        this.hideItem(item);
      }
    });
    
    // Emit event
    eventBus.emit('filter:changed', { type: 'publications', filter });
  }
  
  showCard(card) {
    card.style.display = 'flex';
  }

  hideCard(card) {
    card.style.display = 'none';
  }

  showItem(item) {
    item.style.display = 'flex';
  }

  hideItem(item) {
    item.style.display = 'none';
  }
  
  setupRelatedProjectLinks() {
    const relatedLinks = document.querySelectorAll('.related-projects');
    
    relatedLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        const filter = link.getAttribute('data-filter');
        if (filter) {
          this.currentPage = 1; // Reset to first page
          this.filterProjects(filter);
          
          // Scroll to projects section
          const projectsSection = document.querySelector('#projects');
          if (projectsSection) {
            projectsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    });
  }
  
  setupAbstractToggles() {
    const toggles = document.querySelectorAll('.abstract-toggle');
    
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const abstractContent = toggle.nextElementSibling;
        const icon = toggle.querySelector('i');
        
        toggle.classList.toggle('active');
        abstractContent?.classList.toggle('active');
        
        if (abstractContent?.classList.contains('active')) {
          abstractContent.style.maxHeight = abstractContent.scrollHeight + 'px';
          icon?.classList.replace('fa-chevron-down', 'fa-chevron-up');
        } else {
          abstractContent.style.maxHeight = '0';
          icon?.classList.replace('fa-chevron-up', 'fa-chevron-down');
        }
      });
    });
  }
  
  // Public API
  setProjectFilter(filter) {
    this.currentPage = 1;
    this.filterProjects(filter);
  }
  
  setPublicationFilter(filter) {
    this.filterPublications(filter);
  }
  
  getCurrentPage() {
    return this.currentPage;
  }
  
  getTotalPages() {
    return Math.ceil(this.filteredProjects.length / this.projectsPerPage);
  }
}